// File: src/components/pages/MyAccount.jsx
//
// Profile page — view and edit mode for all authenticated roles:
//   ServiceUser (Student), ServiceProvider (Instructor),
//   ServiceProviderAdmin (Instructor Admin), admin (Admin)
//
// Read-only: email, phone (identity fields managed by Auth0)
// Editable:  all other profile fields relevant to the role

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthToken } from '../../hooks/useAuthToken';
import { User, Mail, Phone, Edit2, Save, X, CheckCircle, AlertCircle, Trash2, Calendar, BookOpen, Building2, Clock } from 'lucide-react';
import { profileAPI } from '../../services/api';
import { AUTH0_NAMESPACE } from '../../utils/constants';
import { toast } from '../../utils/toast';

// ── Theory test expiry helper (mirrors EditStudentModal.jsx) ─────────────────
const isTheoryExpired = (dateStr) => {
  if (!dateStr) return false;
  const passed   = new Date(dateStr);
  const twoYears = new Date();
  twoYears.setFullYear(twoYears.getFullYear() - 2);
  return passed < twoYears;
};

// ── Field components ──────────────────────────────────────────────────────────

const ReadOnlyField = ({ label, value, icon: Icon }) => (
  <div className="form-group">
    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      {Icon && <Icon size={14} color="#9ca3af" />}
      {label}
      <span style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 400 }}>(read-only)</span>
    </label>
    <input
      type="text"
      value={value || '—'}
      disabled
      style={{
        width: '100%', padding: '0.6rem 0.85rem',
        border: '1.5px solid #f3f4f6', borderRadius: '0.5rem',
        background: '#f9fafb', color: '#6b7280',
        fontSize: '0.9rem', fontFamily: 'inherit', cursor: 'not-allowed'
      }}
    />
  </div>
);

const Field = ({ label, value, onChange, isEditing, type = 'text', placeholder = '' }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      disabled={!isEditing}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '0.6rem 0.85rem',
        border: `1.5px solid ${isEditing ? '#667eea' : '#e5e7eb'}`,
        borderRadius: '0.5rem',
        background: isEditing ? '#fff' : '#f9fafb',
        color: isEditing ? '#111827' : '#374151',
        fontSize: '0.9rem', fontFamily: 'inherit',
        outline: 'none', transition: 'border 0.15s'
      }}
    />
  </div>
);

// ── Role label ────────────────────────────────────────────────────────────────

const roleLabel = (role) => ({
  ServiceUser:          'Student',
  ServiceProvider:      'Instructor',
  ServiceProviderAdmin: 'Instructor Admin',
  admin:                'Admin'
}[role] || role);

// ── Main component ────────────────────────────────────────────────────────────

const MyAccount = () => {
  const { user } = useAuth0();
  const getAccessTokenSilently = useAuthToken();
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const primaryRole = ['admin','ServiceProviderAdmin','ServiceProvider','ServiceUser']
    .find(r => userRoles.includes(r)) || 'ServiceUser';

  const [profile,   setProfile]   = useState(null);
  const [form,      setForm]      = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading,       setIsLoading]       = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting,       setIsDeleting]       = useState(false);
  const [deleteSuccess,    setDeleteSuccess]     = useState(false);
  const [isSaving,  setIsSaving]  = useState(false);
  const [error,     setError]     = useState('');

  // ── Load profile ────────────────────────────────────────────────────────────
  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await profileAPI.getProfile(getAccessTokenSilently);
      if (res.success) {
        setProfile(res.data);
        setForm(res.data);
      } else {
        setError(res.error || 'Failed to load profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleField = (key) => (val) => setForm(prev => ({ ...prev, [key]: val }));

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError('');

    // ── Date of Birth validation (students only) ───────────────────────────
    if (isStudent && form.dateOfBirth) {
      const dob  = new Date(form.dateOfBirth);
      const today = new Date();
      const age   = today.getFullYear() - dob.getFullYear()
        - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);

      if (age < 17) {
        setError('You must be at least 17 years old to register or update your profile.');
        return;
      }
    }
    setIsSaving(true);
    try {
      const res = await profileAPI.updateProfile(form, getAccessTokenSilently);
      if (res.success) {
        setProfile({ ...profile, ...form });
        setIsEditing(false);
        toast.success('Profile updated successfully');
      } else {
        setError(res.error || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequest = async () => {
    setIsDeleting(true);
    try {
      const res = await profileAPI.requestDeletion(getAccessTokenSilently);
      if (res.success) {
        setShowDeleteDialog(false);
        setDeleteSuccess(true);
        toast.success('Deletion request submitted. Your instructor will review it shortly.');
      } else {
        setShowDeleteDialog(false);
        setError(res.error || 'Failed to submit deletion request');
      }
    } catch (err) {
      setShowDeleteDialog(false);
      setError(err.message || 'Failed to submit deletion request');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setForm(profile);
    setIsEditing(false);
    setError('');
  };

  // ── Loading / Error state ───────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <User size={48} className="loading-icon" />
          <h2>Loading profile…</h2>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="page-container">
        <div className="error-state">
          <AlertCircle size={48} className="error-icon" />
          <h2>Could not load profile</h2>
          <p className="error-message">{error}</p>
          <button className="btn-primary" onClick={loadProfile}>Retry</button>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  const isStudent = primaryRole === 'ServiceUser';

  // ── shared styles ──────────────────────────────────────────────────────────
  const divider = <hr style={{ border: 'none', borderTop: '1px solid #f3f4f6', margin: '1.5rem 0' }} />;
  const sectionLabel = (text) => (
    <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.75rem' }}>
      {text}
    </p>
  );
  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' };

  return (
    <div className="page-container">

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.75rem', paddingBottom: '1rem',
        borderBottom: '2px solid #e5e7eb', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <User size={28} color="#667eea" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#111827' }}>
              My Account
            </h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
              {roleLabel(primaryRole)} profile
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {!isEditing ? (
            <button className="btn-book-now" onClick={() => setIsEditing(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.1rem' }}>
              <Edit2 size={15} /> Edit Profile
            </button>
          ) : (
            <>
              <button className="btn-cancel" onClick={handleCancel} disabled={isSaving}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <X size={15} /> Cancel
              </button>
              <button className="btn-book-now" onClick={handleSave} disabled={isSaving}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.1rem' }}>
                <Save size={15} /> {isSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.7rem 1rem', marginBottom: '1.25rem',
          background: '#fef2f2', border: '1.5px solid #fca5a5',
          borderRadius: '0.5rem', fontSize: '0.875rem', color: '#991b1b'
        }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* ── Single profile card ───────────────────────────────────────────── */}
      <div style={{
        background: '#fff', borderRadius: '0.75rem',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
        padding: '1.75rem'
      }}>

        {/* Avatar + name row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          {user?.picture ? (
            <img src={user.picture} alt={user.name}
              style={{ width: '60px', height: '60px', borderRadius: '50%',
                       objectFit: 'cover', border: '3px solid #667eea', flexShrink: 0 }} />
          ) : (
            <div style={{
              width: '60px', height: '60px', borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg,#667eea,#764ba2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '1.5rem', fontWeight: 700
            }}>
              {(form.firstName || form.name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
              {form.firstName && form.lastName
                ? `${form.firstName} ${form.lastName}`
                : form.name || user?.name || '—'}
            </div>
            <span style={{
              display: 'inline-block', marginTop: '0.2rem',
              padding: '0.15rem 0.55rem', borderRadius: '999px',
              background: '#667eea22', color: '#4f46e5',
              fontSize: '0.72rem', fontWeight: 700
            }}>
              {roleLabel(primaryRole)}
            </span>
          </div>
        </div>

        {divider}

        {/* ── Account Details ─────────────────────────────────────────────── */}
        {sectionLabel('Account Details')}
        <div style={{ ...grid2, marginBottom: '1rem' }}>
          <ReadOnlyField label="Email" value={form.email} icon={Mail} />
          <ReadOnlyField label="Phone" value={form.phone} icon={Phone} />
        </div>
        <div style={grid2}>
          <Field label="First Name" value={form.firstName} onChange={handleField('firstName')} isEditing={isEditing} />
          <Field label="Last Name"  value={form.lastName}  onChange={handleField('lastName')}  isEditing={isEditing} />
        </div>

        {/* ── Student-specific fields ─────────────────────────────────────── */}
        {isStudent && (
          <>
            {divider}
            {sectionLabel('Personal Details')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    value={form.dateOfBirth || ''}
                    onChange={e => handleField('dateOfBirth')(e.target.value)}
                    disabled={!isEditing}
                    max={(() => {
                      const d = new Date();
                      d.setFullYear(d.getFullYear() - 17);
                      return d.toISOString().split('T')[0];
                    })()}
                    style={{
                      width: '100%', padding: '0.6rem 0.85rem',
                      border: `1.5px solid ${isEditing ? '#667eea' : '#e5e7eb'}`,
                      borderRadius: '0.5rem',
                      background: isEditing ? '#fff' : '#f9fafb',
                      color: isEditing ? '#111827' : '#374151',
                      fontSize: '0.9rem', fontFamily: 'inherit',
                      outline: 'none', transition: 'border 0.15s'
                    }}
                  />
                  {isEditing && (
                    <p style={{ fontSize: '0.76rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      You must be at least 17 years old.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Vehicle Option</label>
                  <select
                    value={form.vehicleOption || ''}
                    onChange={e => handleField('vehicleOption')(e.target.value)}
                    disabled={!isEditing}
                    style={{
                      width: '100%', padding: '0.6rem 0.85rem',
                      border: `1.5px solid ${isEditing ? '#667eea' : '#e5e7eb'}`,
                      borderRadius: '0.5rem',
                      background: isEditing ? '#fff' : '#f9fafb',
                      color: form.vehicleOption ? '#111827' : '#9ca3af',
                      fontSize: '0.9rem', fontFamily: 'inherit',
                      outline: 'none', cursor: isEditing ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <option value="">Select vehicle type</option>
                    <option value="Automatic">Automatic</option>
                    <option value="Manual">Manual</option>
                  </select>
                </div>
              </div>

            {divider}
            {sectionLabel('Pickup Address')}
            <div style={grid2}>
                <Field label="Address" value={form.pickupAddress}  onChange={handleField('pickupAddress')}  isEditing={isEditing} />
                <Field label="Postcode" value={form.pickupPostcode} onChange={handleField('pickupPostcode')} isEditing={isEditing} />
            </div>

            {divider}
            {sectionLabel('Drop-off Address')}
              {isEditing && (
                <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  marginBottom: '0.75rem', cursor: 'pointer',
                  fontSize: '0.875rem', color: '#374151', userSelect: 'none'
                }}>
                  <input
                    type="checkbox"
                    checked={
                      form.dropoffAddress  === form.pickupAddress &&
                      form.dropoffPostcode === form.pickupPostcode &&
                      !!form.pickupAddress
                    }
                    onChange={e => {
                      if (e.target.checked) {
                        setForm(prev => ({
                          ...prev,
                          dropoffAddress:  prev.pickupAddress,
                          dropoffPostcode: prev.pickupPostcode
                        }));
                      } else {
                        setForm(prev => ({
                          ...prev,
                          dropoffAddress:  '',
                          dropoffPostcode: ''
                        }));
                      }
                    }}
                    style={{ accentColor: '#667eea', width: '15px', height: '15px' }}
                  />
                  Same as Pickup
                </label>
              )}
            <div style={grid2}>
                <Field label="Address"  value={form.dropoffAddress}  onChange={handleField('dropoffAddress')}  isEditing={isEditing} />
                <Field label="Postcode" value={form.dropoffPostcode} onChange={handleField('dropoffPostcode')} isEditing={isEditing} />
              </div>

            {divider}
            {sectionLabel('Driving Test Details')}
            {(() => {
              const theoryExpired = isTheoryExpired(form.theoryTestCompletedDate);
              const dateInputStyle = {
                width: '100%', padding: '0.6rem 0.85rem',
                border: `1.5px solid ${isEditing ? '#667eea' : '#e5e7eb'}`,
                borderRadius: '0.5rem',
                background: isEditing ? '#fff' : '#f9fafb',
                color: isEditing ? '#111827' : '#374151',
                fontSize: '0.9rem', fontFamily: 'inherit',
                outline: 'none', transition: 'border 0.15s'
              };
              return (
                <>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <BookOpen size={14} color="#9ca3af" /> Theory Test Passed Date
                      {theoryExpired && (
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 700,
                          color: '#ef4444', background: '#fee2e2',
                          padding: '0.15rem 0.5rem', borderRadius: '999px'
                        }}>
                          Expired
                        </span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={form.theoryTestCompletedDate || ''}
                      onChange={e => handleField('theoryTestCompletedDate')(e.target.value)}
                      disabled={!isEditing}
                      max={new Date().toISOString().split('T')[0]}
                      style={theoryExpired ? { ...dateInputStyle, borderColor: '#ef4444' } : dateInputStyle}
                    />
                    {theoryExpired && (
                      <p style={{ fontSize: '0.76rem', color: '#ef4444', marginTop: '0.25rem' }}>
                        Theory test result is older than 2 years and has expired.
                      </p>
                    )}
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <Field
                      label={<span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Building2 size={14} color="#9ca3af" /> Test Centre</span>}
                      value={form.testCenter}
                      onChange={handleField('testCenter')}
                      isEditing={isEditing}
                      placeholder="e.g. Salisbury Test Centre"
                    />
                  </div>

                  <div style={grid2}>
                    <div className="form-group">
                      <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Calendar size={14} color="#9ca3af" /> Test Date
                      </label>
                      <input
                        type="date"
                        value={form.testDate || ''}
                        onChange={e => handleField('testDate')(e.target.value)}
                        disabled={!isEditing}
                        min={new Date().toISOString().split('T')[0]}
                        style={dateInputStyle}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Clock size={14} color="#9ca3af" /> Test Time
                      </label>
                      <input
                        type="time"
                        value={form.testTime || ''}
                        onChange={e => handleField('testTime')(e.target.value)}
                        disabled={!isEditing}
                        style={dateInputStyle}
                      />
                    </div>
                  </div>
                </>
              );
            })()}
          </>
        )}

        {/* ── Instructor / Admin fields ───────────────────────────────────── */}
        {!isStudent && (
          <>
            {divider}
            {sectionLabel('Business Details')}
            <div style={grid2}>
              <Field label="Business Name" value={form.businessName} onChange={handleField('businessName')} isEditing={isEditing} />
              <Field label="Postcode"      value={form.postcode}     onChange={handleField('postcode')}     isEditing={isEditing} />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <Field label="Address" value={form.address} onChange={handleField('address')} isEditing={isEditing} />
            </div>
          </>
        )}

        {/* ── Delete Account (students only) ──────────────────────────── */}
        {isStudent && !deleteSuccess && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid #fee2e2', margin: '2rem 0 1.5rem' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontWeight: 600, color: '#991b1b', fontSize: '0.9rem', margin: 0 }}>
                  Delete Account
                </p>
                <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0.2rem 0 0' }}>
                  Submit a request to permanently remove your account.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteDialog(true)}
                disabled={isEditing}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.5rem 1rem', borderRadius: '0.5rem',
                  border: '1.5px solid #fca5a5', background: '#fff',
                  color: '#dc2626', fontSize: '0.85rem', fontWeight: 600,
                  cursor: isEditing ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', opacity: isEditing ? 0.5 : 1
                }}
              >
                <Trash2 size={15} /> Delete Account
              </button>
            </div>
          </>
        )}

        {/* Deletion request already submitted */}
        {isStudent && deleteSuccess && (
          <div style={{
            marginTop: '1.5rem', padding: '0.9rem 1rem',
            background: '#fef9c3', border: '1.5px solid #fde047',
            borderRadius: '0.5rem', fontSize: '0.875rem', color: '#854d0e',
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem'
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
            <span>
              <strong>Deletion request submitted.</strong> Your instructor will review it and complete
              the process. You may continue using the app until your account is removed.
            </span>
          </div>
        )}

      </div>

      {/* ── Confirmation dialog ───────────────────────────────────────────── */}
      {showDeleteDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff', borderRadius: '0.75rem', maxWidth: '480px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '1.75rem'
          }}>
            {/* Dialog header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '1rem' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Trash2 size={18} color="#dc2626" />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>
                Delete Account
              </h3>
            </div>

            {/* Warning message */}
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: '0.5rem', padding: '0.9rem 1rem',
              marginBottom: '1.25rem'
            }}>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#7f1d1d', lineHeight: 1.6 }}>
                <strong>Warning:</strong> This action will request the deletion of your account.
                Once approved by your instructor, all of your personal information and associated
                data will be permanently removed from the system. After your account has been
                deleted, you may register again with a different instructor if you wish.
              </p>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#7f1d1d', fontWeight: 600 }}>
                Are you sure you want to submit this request?
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                style={{
                  padding: '0.55rem 1.1rem', borderRadius: '0.5rem',
                  border: '1.5px solid #e5e7eb', background: '#fff',
                  color: '#374151', fontSize: '0.875rem', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRequest}
                disabled={isDeleting}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.55rem 1.1rem', borderRadius: '0.5rem',
                  border: 'none', background: '#dc2626',
                  color: '#fff', fontSize: '0.875rem', fontWeight: 600,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', opacity: isDeleting ? 0.7 : 1
                }}
              >
                <Trash2 size={15} />
                {isDeleting ? 'Submitting…' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccount;